import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowDown, ArrowLeft, ArrowUp, CalendarDays, Check, FileDown, KeyRound, Landmark, Loader2, LogOut, Mail, Pencil, Plus, Receipt, RefreshCw, Trash2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { evalMoneyExpr, moneyToIsk, fmtIsk, fmtNum2, fmtUsdApprox, ISK_PER_USD } from "@/lib/price";
import { DualPrice } from "@/components/DualPrice";

const inputCls =
  "border border-amber-900/40 bg-[#0d0d12] px-3 py-2.5 text-sm text-amber-50 placeholder:text-neutral-600 outline-none transition-colors focus:border-amber-400";

const today = () => new Date().toISOString().slice(0, 10);

const fmtD = (iso) => {
  if (!iso) return "";
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

const toIsk = (str) => Math.round(moneyToIsk(String(str || "")) || 0);

const emptyLine = () => ({ description: "", qty: "1", unit: "", vat_rate: 11 });

const blankInvoice = () => ({
  id: null,
  invoice_number: "",
  client_name: "",
  client_email: "",
  client_address: "",
  lines: [emptyLine()],
  deposit: "",
  issue_date: today(),
  due_date: "",
  notes: "",
  trip_id: "",
});

// prices are VAT-inclusive; the VAT shown is the portion contained in the price
const computeTotals = (lines, deposit) => {
  let subtotal = 0;
  const vat = {};
  for (const l of lines) {
    const net = Math.round((parseFloat(l.qty) || 1) * toIsk(l.unit));
    subtotal += net;
    const rate = Number(l.vat_rate) || 0;
    if (rate > 0) vat[rate] = (vat[rate] || 0) + Math.round((net * rate) / (100 + rate));
  }
  const dep = toIsk(deposit);
  return { subtotal, vat, dep, balance: subtotal - dep };
};

export default function OwnerInvoices() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const token = localStorage.getItem("er-owner-token") || "";
  const headers = { Authorization: `Bearer ${token}` };

  const [invoices, setInvoices] = useState(null);
  const [editing, setEditing] = useState(undefined); // undefined = list
  const [saving, setSaving] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState("");
  const [paymentDetailsLoaded, setPaymentDetailsLoaded] = useState(false);
  const [vatCheck, setVatCheck] = useState(null);
  const [confirmDelId, setConfirmDelId] = useState(""); // invoice id awaiting the second tap
  const tripPrefill = searchParams.get("trip") || "";

  const load = () => {
    axios.get(`${API}/owner/invoices`, { headers }).then((res) => setInvoices(res.data.invoices)).catch(() => setInvoices([]));
  };

  useEffect(() => {
    if (token) {
      load();
      axios
        .get(`${API}/owner/invoice-payment-details`, { headers })
        .then((res) => {
          setPaymentDetails(res.data.details || "");
          setPaymentDetailsLoaded(true);
        })
        .catch(() => setPaymentDetailsLoaded(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // "Invoice" from a trip card lands here — prefill a fresh invoice from the itinerary
  useEffect(() => {
    if (!token || !tripPrefill || editing !== undefined) return;
    axios
      .get(`${API}/owner/trips/${tripPrefill}/invoice-prefill`, { headers })
      .then((res) => {
        const p = res.data;
        setEditing({
          ...blankInvoice(),
          trip_id: p.trip_id,
          client_name: p.client_name,
          client_email: p.client_email,
          lines: p.lines.length
            ? p.lines.map((l) => ({ description: l.description, qty: String(l.qty), unit: fmtNum2(l.unit_price_isk), vat_rate: l.vat_rate }))
            : [emptyLine()],
          deposit: p.deposit_isk ? fmtNum2(p.deposit_isk) : "",
        });
        toast.success("Invoice prefilled from the itinerary — adjust anything");
      })
      .catch(() => toast.error("Could not prefill from that trip."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripPrefill, token]);

  const openEdit = (inv) => {
    setEditing({
      id: inv.id,
      invoice_number: inv.invoice_number,
      client_name: inv.client_name,
      client_email: inv.client_email || "",
      client_address: inv.client_address || "",
      lines: inv.lines.length
        ? inv.lines.map((l) => ({ description: l.description, qty: String(l.qty), unit: fmtNum2(l.unit_price_isk), vat_rate: l.vat_rate }))
        : [emptyLine()],
      deposit: inv.deposit_isk ? fmtNum2(inv.deposit_isk) : "",
      issue_date: inv.issue_date || today(),
      due_date: inv.due_date || "",
      notes: inv.notes || "",
      trip_id: inv.trip_id || "",
    });
  };

  const setLine = (i, key, v) =>
    setEditing((e) => ({ ...e, lines: e.lines.map((l, li) => (li === i ? { ...l, [key]: v } : l)) }));
  const moveLine = (i, dir) =>
    setEditing((e) => {
      const j = i + dir;
      if (j < 0 || j >= e.lines.length) return e;
      const lines = [...e.lines];
      [lines[i], lines[j]] = [lines[j], lines[i]];
      return { ...e, lines };
    });

  const totals = useMemo(() => (editing ? computeTotals(editing.lines, editing.deposit) : null), [editing]);

  // saved invoices for the same trip — they are snapshots; this editor may be fresher
  const siblingInvoices = editing?.trip_id ? (invoices || []).filter((i) => i.trip_id === editing.trip_id && i.id !== editing.id) : [];

  // re-pull the lines from the itinerary as it stands right now (itinerary edits never rewrite saved invoices)
  const refreshFromItinerary = async () => {
    if (!editing?.trip_id) return;
    try {
      const res = await axios.get(`${API}/owner/trips/${editing.trip_id}/invoice-prefill`, { headers });
      const p = res.data;
      setEditing((e) => ({
        ...e,
        client_name: p.client_name,
        client_email: p.client_email,
        lines: p.lines.length
          ? p.lines.map((l) => ({ description: l.description, qty: String(l.qty), unit: fmtNum2(l.unit_price_isk), vat_rate: l.vat_rate }))
          : [emptyLine()],
        deposit: p.deposit_isk ? fmtNum2(p.deposit_isk) : "",
      }));
      toast.success("Lines refreshed from the itinerary as it is now — review before saving");
    } catch {
      toast.error("Could not pull the itinerary right now.");
    }
  };

  // VAT check: what this invoice charges the client vs the VAT credit from vendor invoices on the trip
  useEffect(() => {
    setVatCheck(null);
    if (!editing?.trip_id) return;
    axios
      .get(`${API}/owner/trips/${editing.trip_id}/vat-summary`, { headers })
      .then((res) => setVatCheck(res.data))
      .catch(() => setVatCheck(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing?.trip_id]);

  const save = async () => {
    if (!editing.client_name.trim() || saving) return null;
    setSaving(true);
    const payload = {
      client_name: editing.client_name,
      client_email: editing.client_email,
      client_address: editing.client_address,
      lines: editing.lines
        .filter((l) => l.description.trim())
        .map((l) => ({ description: l.description, qty: parseFloat(l.qty) || 1, unit_price_isk: toIsk(l.unit), vat_rate: Number(l.vat_rate) })),
      deposit_isk: toIsk(editing.deposit),
      issue_date: editing.issue_date,
      due_date: editing.due_date,
      notes: editing.notes,
      trip_id: editing.trip_id,
    };
    try {
      const res = editing.id
        ? await axios.put(`${API}/owner/invoices/${editing.id}`, payload, { headers })
        : await axios.post(`${API}/owner/invoices`, payload, { headers });
      toast.success(`Invoice ${res.data.invoice_number} saved`);
      return res.data;
    } catch {
      toast.error("Could not save the invoice.");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async (inv) => {
    try {
      const res = await axios.get(`${API}/owner/invoices/${inv.id}/pdf`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${inv.invoice_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not generate the PDF.");
    }
  };

  const saveAndPdf = async () => {
    const saved = await save();
    if (!saved) return;
    setEditing(undefined);
    setSearchParams({});
    load();
    await downloadPdf(saved);
  };

  const remove = async (id) => {
    if (confirmDelId !== id) {
      setConfirmDelId(id);
      setTimeout(() => setConfirmDelId((c) => (c === id ? "" : c)), 4000);
      return;
    }
    setConfirmDelId("");
    try {
      await axios.delete(`${API}/owner/invoices/${id}`, { headers });
      toast.success("Invoice deleted — its number is free again");
      load();
    } catch {
      toast.error("Could not delete the invoice.");
    }
  };

  const togglePaid = async (inv) => {
    const next = inv.status === "paid" ? "unpaid" : "paid";
    try {
      await axios.post(`${API}/owner/invoices/${inv.id}/status`, { status: next }, { headers });
      toast.success(next === "paid" ? `${inv.invoice_number} marked paid` : `${inv.invoice_number} back to unpaid`);
      load();
    } catch {
      toast.error("Could not update the invoice.");
    }
  };

  // a credit note (kreditnota) is the correct way to undo an invoice that has already been
  // sent — lines and deposit are negated, it gets its own KR number and can be edited,
  // emailed and PDF'd like any other document
  const creditNote = async (inv) => {
    try {
      const res = await axios.post(`${API}/owner/invoices/${inv.id}/credit-note`, {}, { headers });
      toast.success(`Credit note ${res.data.invoice_number} created for ${inv.invoice_number}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create the credit note.");
    }
  };

  const emailInvoice = async (inv) => {
    try {
      await axios.post(`${API}/owner/invoices/${inv.id}/email`, {}, { headers });
      toast.success(`Invoice emailed to ${inv.client_email}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send the email right now.");
    }
  };

  const savePaymentDetails = async () => {
    try {
      await axios.put(`${API}/owner/invoice-payment-details`, { details: paymentDetails }, { headers });
      toast.success("Payment details saved — they go on every invoice PDF and email");
    } catch {
      toast.error("Could not save the payment details.");
    }
  };

  if (!token) {
    return (
      <div data-testid="invoices-locked" className="flex min-h-screen flex-col items-center justify-center bg-[#070709] px-5 text-center">
        <img src="/assets/logo.png" alt="Exclusive Reykjavik" className="h-16 w-auto mix-blend-screen" />
        <p className="mt-8 font-serif text-3xl font-light text-neutral-200">The private door is closed.</p>
        <p className="mt-3 max-w-sm text-sm text-neutral-500">Sign in at the concierge desk first — your key opens the invoices too.</p>
        <Link
          to="/owner"
          data-testid="invoices-goto-login"
          className="mt-8 flex items-center gap-2 bg-amber-500 px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-colors hover:bg-amber-300"
        >
          <KeyRound className="h-4 w-4" /> Go to Sign-In
        </Link>
      </div>
    );
  }

  if (editing !== undefined) {
    return (
      <div data-testid="invoice-editor" className="min-h-screen bg-[#070709] text-neutral-100">
        <header className="sticky top-0 z-40 border-b border-amber-500/15 bg-[#0c0c10]/90 backdrop-blur-xl">
          <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-3 px-5 py-4">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">Invoicing</p>
              <h1 className="font-serif text-xl font-light text-neutral-50">
                {editing.id ? `Invoice ${editing.invoice_number}` : "New invoice"}
              </h1>
            </div>
            <button
              data-testid="invoice-editor-back"
              onClick={() => {
                setEditing(undefined);
                setSearchParams({});
              }}
              className="flex items-center gap-2 border border-neutral-700 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> All invoices
            </button>
          </div>
        </header>

        <main className="mx-auto max-w-4xl px-5 py-8">
          {editing.trip_id && !editing.id && (
            <p data-testid="invoice-prefill-note" className="mb-6 border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-amber-200/90">
              Prefilled from the itinerary — adjust anything before saving
            </p>
          )}
          {siblingInvoices.length > 0 && (
            <p data-testid="invoice-snapshot-warning" className="mb-6 border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-amber-200/90">
              Saved {siblingInvoices.length === 1 ? "invoice" : "invoices"} for this trip:{" "}
              {siblingInvoices.map((s) => `${s.invoice_number} (${s.issue_date})`).join(", ")} — saved invoices are snapshots and do not follow itinerary edits.
              {editing.id ? " Press “From itinerary” below to re-pull today's lines into this one." : " This draft is fresh from the itinerary as it is now."}
            </p>
          )}
          {editing.id && editing.trip_id && siblingInvoices.length === 0 && (
            <p data-testid="invoice-snapshot-note" className="mb-6 border border-neutral-700/60 bg-neutral-900/40 px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-neutral-400">
              Saved invoice — a snapshot. If the itinerary changed since, press “From itinerary” below to re-pull today's lines.
            </p>
          )}

          <div className="border border-amber-500/20 bg-[#0c0c10] p-5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">Billed to</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <input data-testid="invoice-client-name" placeholder="Client name *" value={editing.client_name} onChange={(e) => setEditing((x) => ({ ...x, client_name: e.target.value }))} className={inputCls} />
              <input data-testid="invoice-client-email" placeholder="Client email" value={editing.client_email} onChange={(e) => setEditing((x) => ({ ...x, client_email: e.target.value }))} className={inputCls} />
            </div>
            <textarea
              data-testid="invoice-client-address"
              placeholder="Billing address (optional)"
              value={editing.client_address}
              onChange={(e) => setEditing((x) => ({ ...x, client_address: e.target.value }))}
              className={`${inputCls} mt-3 w-full`}
              rows={2}
            />
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="text-[9px] uppercase tracking-[0.2em] text-neutral-500">Issue date</span>
                <input data-testid="invoice-issue-date" type="date" value={editing.issue_date} onChange={(e) => setEditing((x) => ({ ...x, issue_date: e.target.value }))} className={`${inputCls} mt-1.5 w-full`} />
              </label>
              <label className="block">
                <span className="text-[9px] uppercase tracking-[0.2em] text-neutral-500">Due date</span>
                <input data-testid="invoice-due-date" type="date" min={editing.issue_date || undefined} value={editing.due_date} onChange={(e) => setEditing((x) => ({ ...x, due_date: e.target.value }))} className={`${inputCls} mt-1.5 w-full`} />
              </label>
            </div>
          </div>

          <div className="mt-6 border border-amber-500/20 bg-[#0c0c10] p-5">
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">Lines — prices include VAT</p>
              <div className="flex items-center gap-2">
                {editing.trip_id && (
                  <button
                    data-testid="invoice-refresh-lines"
                    onClick={refreshFromItinerary}
                    title="Replace the lines with the itinerary as it is right now"
                    className="flex items-center gap-1.5 border border-neutral-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
                  >
                    <RefreshCw className="h-3 w-3" /> From itinerary
                  </button>
                )}
                <button
                  data-testid="invoice-add-line"
                  onClick={() => setEditing((x) => ({ ...x, lines: [...x.lines, emptyLine()] }))}
                  className="flex items-center gap-1.5 border border-neutral-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
                >
                  <Plus className="h-3 w-3" /> Add line
                </button>
              </div>
            </div>
            <div className="mt-4 space-y-2.5">
              {editing.lines.map((l, i) => (
                <div key={i} data-testid={`invoice-line-${i}`} className="flex flex-wrap items-center gap-2">
                  <input
                    data-testid={`invoice-line-desc-${i}`}
                    placeholder="Description — e.g. Snowmobiling, 2 pax"
                    value={l.description}
                    onChange={(e) => setLine(i, "description", e.target.value)}
                    className={`${inputCls} min-w-40 flex-[2]`}
                  />
                  <input
                    data-testid={`invoice-line-qty-${i}`}
                    placeholder="Qty"
                    value={l.qty}
                    onChange={(e) => setLine(i, "qty", e.target.value)}
                    className={`${inputCls} w-16`}
                  />
                  <input
                    data-testid={`invoice-line-price-${i}`}
                    placeholder="Price ISK"
                    value={l.unit}
                    onChange={(e) => setLine(i, "unit", e.target.value)}
                    onBlur={(e) => {
                      const v = evalMoneyExpr(e.target.value);
                      if (v) setLine(i, "unit", v);
                    }}
                    className={`${inputCls} w-32`}
                  />
                  <select
                    data-testid={`invoice-line-vat-${i}`}
                    value={l.vat_rate}
                    onChange={(e) => setLine(i, "vat_rate", Number(e.target.value))}
                    className={`${inputCls} w-24`}
                  >
                    <option value={11}>11% VSK</option>
                    <option value={24}>24% VSK</option>
                    <option value={0}>No VAT</option>
                  </select>
                  <span data-testid={`invoice-line-amount-${i}`} className="w-28 text-right font-luxe text-sm tracking-wide text-amber-100">
                    {fmtIsk(Math.round((parseFloat(l.qty) || 1) * toIsk(l.unit)))}
                  </span>
                  <button
                    data-testid={`invoice-line-up-${i}`}
                    onClick={() => moveLine(i, -1)}
                    disabled={i === 0}
                    title="Move line up"
                    className="text-neutral-600 transition-colors hover:text-amber-300 disabled:opacity-20"
                    aria-label="Move line up"
                  >
                    <ArrowUp className="h-4 w-4" />
                  </button>
                  <button
                    data-testid={`invoice-line-down-${i}`}
                    onClick={() => moveLine(i, 1)}
                    disabled={i === editing.lines.length - 1}
                    title="Move line down"
                    className="text-neutral-600 transition-colors hover:text-amber-300 disabled:opacity-20"
                    aria-label="Move line down"
                  >
                    <ArrowDown className="h-4 w-4" />
                  </button>
                  <button
                    data-testid={`invoice-line-remove-${i}`}
                    onClick={() => setEditing((x) => ({ ...x, lines: x.lines.filter((_, li) => li !== i) }))}
                    className="text-neutral-600 transition-colors hover:text-red-400"
                    aria-label="Remove line"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-amber-500/10 pt-4">
              <span className="text-[9px] uppercase tracking-[0.2em] text-neutral-500">Deposit already paid (ISK, deducted)</span>
              <input
                data-testid="invoice-deposit"
                placeholder="0"
                value={editing.deposit}
                onChange={(e) => setEditing((x) => ({ ...x, deposit: e.target.value }))}
                onBlur={(e) => {
                  const v = evalMoneyExpr(e.target.value);
                  if (v) setEditing((x) => ({ ...x, deposit: v }));
                }}
                className={`${inputCls} w-32`}
              />
            </div>

            <div data-testid="invoice-totals" className="mt-5 space-y-1.5 border-t border-amber-500/10 pt-4 text-right">
              <p className="text-sm text-neutral-400">
                Subtotal (VAT included) <span data-testid="invoice-subtotal" className="ml-4 font-luxe tracking-wide text-amber-100">{fmtIsk(totals.subtotal)}</span>
              </p>
              {Object.entries(totals.vat).map(([rate, amount]) => (
                <p key={rate} data-testid={`invoice-vat-${rate}`} className="text-xs text-neutral-500">
                  Of which VSK {rate}% <span className="ml-4">{fmtIsk(amount)}</span>
                </p>
              ))}
              <p className="text-sm text-neutral-400">
                Total without VAT <span data-testid="invoice-net" className="ml-4 font-luxe tracking-wide text-neutral-200">{fmtIsk(totals.subtotal - Object.values(totals.vat).reduce((a, b) => a + b, 0))}</span>
              </p>
              {totals.dep > 0 && (
                <p data-testid="invoice-deposit-line" className="text-sm text-neutral-400">
                  Deposit already paid <span className="ml-4 font-luxe tracking-wide text-emerald-200">− {fmtIsk(totals.dep)}</span>
                </p>
              )}
              <p className="pt-1 text-base font-semibold text-neutral-100">
                Balance due{" "}
                <span data-testid="invoice-balance" className="ml-4 font-luxe text-xl tracking-wide text-amber-100">
                  {fmtIsk(totals.balance)} <span className="text-sm font-normal text-neutral-500">{fmtUsdApprox(totals.balance / ISK_PER_USD)}</span>
                </span>
              </p>
            </div>

            {vatCheck &&
              (() => {
                const vatOut = Object.values(totals.vat).reduce((a, b) => a + b, 0);
                return (
                  <div data-testid="invoice-vat-check" className="mt-5 border border-amber-500/25 bg-amber-500/5 px-4 py-3">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-amber-300/80">VAT check — client invoice vs vendor invoices</p>
                    <div className="mt-2 space-y-1 text-xs">
                      <p className="flex items-center justify-between gap-3">
                        <span className="text-neutral-400">VAT you charge the client (this invoice)</span>
                        <span data-testid="invoice-vat-out" className="font-luxe tracking-wide text-amber-100">{fmtIsk(vatOut)}</span>
                      </p>
                      <p className="flex items-center justify-between gap-3">
                        <span className="text-neutral-400">VAT credit from attached vendor invoices</span>
                        <span data-testid="invoice-vat-credit" className="font-luxe tracking-wide text-emerald-200">− {fmtIsk(vatCheck.credit_isk)}</span>
                      </p>
                      <p className="flex items-center justify-between gap-3 border-t border-amber-500/10 pt-1">
                        <span className="text-neutral-200">Estimated left to pay the tax office</span>
                        <span data-testid="invoice-vat-diff" className="font-luxe tracking-wide text-amber-200">{fmtIsk(vatOut - vatCheck.credit_isk)}</span>
                      </p>
                    </div>
                    {vatCheck.missing.length > 0 && (
                      <p data-testid="invoice-vat-missing" className="mt-2 border border-red-500/40 bg-red-950/20 px-3 py-2 text-[10px] uppercase leading-relaxed tracking-[0.15em] text-red-200/90">
                        {vatCheck.missing.length} {vatCheck.missing.length === 1 ? "job has" : "jobs have"} no vendor invoice yet:{" "}
                        {vatCheck.missing.map((m) => m.title).join(" · ")}
                      </p>
                    )}
                  </div>
                );
              })()}

            <textarea
              data-testid="invoice-notes"
              placeholder="Notes on the invoice — payment instructions, reference…"
              value={editing.notes}
              onChange={(e) => setEditing((x) => ({ ...x, notes: e.target.value }))}
              className={`${inputCls} mt-4 w-full`}
              rows={2}
            />
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              data-testid="invoice-save-button"
              onClick={async () => {
                const saved = await save();
                if (saved) {
                  setEditing(undefined);
                  setSearchParams({});
                  load();
                }
              }}
              disabled={saving || !editing.client_name.trim()}
              className="flex items-center gap-2 border border-amber-500/60 px-6 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Receipt className="h-4 w-4" />} Save invoice
            </button>
            <button
              data-testid="invoice-save-pdf-button"
              onClick={saveAndPdf}
              disabled={saving || !editing.client_name.trim()}
              className="flex items-center gap-2 bg-amber-500 px-6 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-colors hover:bg-amber-300 disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} Save & download PDF
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div data-testid="invoices-page" className="min-h-screen bg-[#070709] text-neutral-100">
      <header className="sticky top-0 z-40 border-b border-amber-500/15 bg-[#0c0c10]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-5 py-4">
          <div className="flex items-center gap-4">
            <img src="/assets/logo.png" alt="" className="h-10 w-auto mix-blend-screen" />
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">Concierge Desk</p>
              <h1 className="font-serif text-xl font-light text-neutral-50">Invoices</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/owner/calendar" data-testid="invoices-goto-calendar" className="flex items-center gap-2 border border-neutral-700 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200">
              <CalendarDays className="h-3.5 w-3.5" /> Calendar
            </Link>
            <Link
              to="/owner/vat"
              data-testid="invoices-goto-vat"
              className="flex items-center gap-2 border border-neutral-700 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
            >
              <Landmark className="h-3.5 w-3.5" /> VAT Report
            </Link>
            <Link to="/owner/trips" data-testid="invoices-goto-trips" className="flex items-center gap-2 border border-neutral-700 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200">
              Trip Builder
            </Link>
            <button
              data-testid="invoices-logout-button"
              onClick={() => {
                localStorage.removeItem("er-owner-token");
                navigate("/owner");
              }}
              className="flex items-center gap-2 border border-neutral-700 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-red-400 hover:text-red-300"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 py-10">
        {(() => {
          const open = (invoices || []).filter((i) => i.status !== "paid" && i.totals.balance_isk > 0);
          return open.length > 0 ? (
            <div data-testid="invoices-outstanding" className="mb-6 flex flex-wrap items-center justify-between gap-2 border border-amber-500/30 bg-amber-500/5 px-4 py-3">
              <p className="text-[10px] uppercase tracking-[0.25em] text-amber-200/90">
                Outstanding — {open.length} open invoice{open.length === 1 ? "" : "s"}
              </p>
              <p className="font-luxe text-xl tracking-wide text-amber-100">
                <DualPrice value={`${fmtNum2(open.reduce((a, i) => a + i.totals.balance_isk, 0))} ISK`} />
              </p>
            </div>
          ) : null;
        })()}

        <div className="mb-6 border border-amber-500/20 bg-[#0c0c10] p-4">
          <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">
            <Landmark className="h-3.5 w-3.5" /> Payment details on your invoices
          </p>
          <p className="mt-1 text-xs text-neutral-500">Your kt and bank account — printed on every invoice PDF and email so clients can pay by transfer.</p>
          <textarea
            data-testid="payment-details-input"
            placeholder={"kt 123456-7890\nLandsbankinn 0123-45-678901"}
            value={paymentDetails}
            disabled={!paymentDetailsLoaded}
            onChange={(e) => setPaymentDetails(e.target.value)}
            className={`${inputCls} mt-3 w-full`}
            rows={2}
          />
          <button
            data-testid="payment-details-save"
            onClick={savePaymentDetails}
            className="mt-2 border border-amber-500/60 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black"
          >
            Save payment details
          </button>
        </div>

        <button
          data-testid="invoice-new-button"
          onClick={() => setEditing(blankInvoice())}
          className="flex items-center gap-2 bg-amber-500 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-colors hover:bg-amber-300"
        >
          <Plus className="h-4 w-4" /> New invoice
        </button>

        <div className="mt-8 divide-y divide-neutral-800/70">
          {invoices === null ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-amber-400" />
            </div>
          ) : invoices.length === 0 ? (
            <div data-testid="invoices-empty" className="border border-dashed border-neutral-700 py-16 text-center">
              <p className="font-serif text-2xl font-light text-neutral-400">No invoices yet.</p>
              <p className="mt-2 text-sm text-neutral-600">Make one from a trip card, or start a custom one here.</p>
            </div>
          ) : (
            invoices.map((inv) => (
              <div key={inv.id} data-testid={`invoice-row-${inv.id}`} className="flex flex-wrap items-center justify-between gap-3 py-4">
                <div className="min-w-0">
                  <p className="font-serif text-lg leading-tight text-neutral-50">
                    <span data-testid={`invoice-number-${inv.id}`} className="mr-3 text-sm tracking-[0.15em] text-amber-300/90">{inv.invoice_number}</span>
                    {inv.client_name}
                    {inv.status === "paid" && (
                      <span data-testid={`invoice-paid-badge-${inv.id}`} className="ml-3 border border-emerald-400/40 bg-emerald-500/10 px-2 py-0.5 text-[9px] uppercase tracking-[0.2em] text-emerald-200">
                        Paid
                      </span>
                    )}
                    {inv.doc_type === "credit_note" && (
                      <span data-testid={`invoice-credit-badge-${inv.id}`} className="ml-3 border border-sky-500/40 bg-sky-500/10 px-2 py-0.5 text-[9px] uppercase tracking-[0.2em] text-sky-200">
                        Kreditnota{inv.credit_for ? ` · ${inv.credit_for}` : ""}
                      </span>
                    )}
                  </p>
                  <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-neutral-500">
                    issued {fmtD(inv.issue_date)}
                    {inv.due_date ? ` · due ${fmtD(inv.due_date)}` : ""} · {inv.lines.length} line{inv.lines.length === 1 ? "" : "s"}
                    {inv.emailed_at ? " · emailed" : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span data-testid={`invoice-balance-${inv.id}`} className={`mr-2 font-luxe text-lg tracking-wide ${inv.status === "paid" ? "text-neutral-500 line-through" : "text-amber-100"}`}>
                    <DualPrice value={`${fmtNum2(inv.totals.balance_isk)} ISK`} />
                  </span>
                  <button
                    data-testid={`invoice-status-${inv.id}`}
                    onClick={() => togglePaid(inv)}
                    className={`flex items-center gap-1.5 border px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] transition-colors ${
                      inv.status === "paid"
                        ? "border-emerald-500/50 text-emerald-200 hover:border-red-400 hover:text-red-300"
                        : "border-neutral-700 text-neutral-300 hover:border-emerald-400 hover:text-emerald-200"
                    }`}
                  >
                    <Check className="h-3 w-3" /> {inv.status === "paid" ? "Paid" : "Mark paid"}
                  </button>
                  <button
                    data-testid={`invoice-email-${inv.id}`}
                    onClick={() => emailInvoice(inv)}
                    disabled={!inv.client_email}
                    title={inv.client_email ? `Email to ${inv.client_email}` : "Add the client's email to the invoice first"}
                    className="flex items-center gap-1.5 border border-neutral-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200 disabled:opacity-40 disabled:hover:border-neutral-700 disabled:hover:text-neutral-300"
                  >
                    <Mail className="h-3 w-3" /> {inv.emailed_at ? "Resend" : "Email"}
                  </button>
                  <button
                    data-testid={`invoice-edit-${inv.id}`}
                    onClick={() => openEdit(inv)}
                    className="flex items-center gap-1.5 border border-neutral-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
                  >
                    <Pencil className="h-3 w-3" /> Edit
                  </button>
                  <button
                    data-testid={`invoice-pdf-${inv.id}`}
                    onClick={() => downloadPdf(inv)}
                    className="flex items-center gap-1.5 border border-neutral-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
                  >
                    <FileDown className="h-3 w-3" /> PDF
                  </button>
                  {inv.doc_type !== "credit_note" && (
                    <button
                      data-testid={`invoice-credit-${inv.id}`}
                      onClick={() => creditNote(inv)}
                      title="Issue a credit note (kreditnota) for this invoice — the right way to undo an invoice that has already been sent"
                      className="flex items-center gap-1.5 border border-sky-500/40 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-sky-200/90 transition-colors hover:bg-sky-500 hover:text-black"
                    >
                      <Undo2 className="h-3 w-3" /> Credit
                    </button>
                  )}
                  <button
                    data-testid={`invoice-delete-${inv.id}`}
                    onClick={() => remove(inv.id)}
                    title={confirmDelId === inv.id ? "Tap again to really delete — the number is freed for reuse" : "Delete this invoice"}
                    className={`flex items-center gap-1 border px-2 py-1 text-[9px] uppercase tracking-[0.15em] transition-colors ${
                      confirmDelId === inv.id
                        ? "animate-pulse border-red-400 bg-red-500 text-black"
                        : "border-transparent text-neutral-600 hover:border-red-900/50 hover:text-red-400"
                    }`}
                    aria-label={confirmDelId === inv.id ? "Confirm delete invoice" : "Delete invoice"}
                  >
                    <Trash2 className="h-4 w-4" /> {confirmDelId === inv.id ? "Sure?" : ""}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
